import { useMemo, useEffect } from 'react';
import { useDropzone, Accept } from 'react-dropzone';
import { Upload, Loader, FileText, RefreshCw, Trash2 } from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { cn } from '@/lib/utils.ts';

const IMAGE_URL_REGEX = /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i;

interface PrimitiveFileInputProps {
  onDrop: (acceptedFiles: File[]) => void;
  accept?: Accept;
  multiple?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  hint?: string;
  className?: string;
  file?: File;
  fileUrl?: string;
  fileName?: string;
  onRemove?: () => void;
}

const PrimitiveFileInput = ({
  onDrop,
  accept,
  multiple = false,
  disabled = false,
  isLoading = false,
  loadingText = 'Przesyłanie...',
  hint,
  className,
  file,
  fileUrl,
  fileName,
  onRemove,
}: PrimitiveFileInputProps) => {
  const hasPreview = !!file || !!fileUrl;

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept,
    multiple,
    disabled: disabled || isLoading,
    noClick: hasPreview,
    noKeyboard: hasPreview,
    noDrag: hasPreview,
  });

  const objectUrl = useMemo(() => {
    if (file) return URL.createObjectURL(file);
    return undefined;
  }, [file]);

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const previewSrc = objectUrl ?? fileUrl;
  const isImage =
    file?.type.startsWith('image/') ||
    (!file && fileUrl && IMAGE_URL_REGEX.test(fileUrl)) ||
    (!file &&
      fileUrl &&
      accept &&
      Object.keys(accept).every((mime) => mime.startsWith('image/')));

  return (
    <div
      {...getRootProps()}
      className={cn(
        'relative flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed px-6 py-10 transition-colors',
        isDragActive
          ? 'bg-accent-50 border-accent-600'
          : 'border-neutral-200 bg-basic-white hover:border-neutral-300',
        (disabled || isLoading) && 'pointer-events-none opacity-60',
        className,
      )}
    >
      <input {...getInputProps()} />
      {hasPreview ? (
        <>
          <div className="absolute right-2 top-2 flex flex-row gap-1">
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                open();
              }}
              disabled={disabled}
            >
              <RefreshCw />
              Zmień
            </ActionButton>

            {onRemove && (
              <ActionButton
                iconOnly
                tone="danger"
                size="sm"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                disabled={disabled}
              >
                <Trash2 />
              </ActionButton>
            )}
          </div>

          {isImage && previewSrc ? (
            <img
              src={previewSrc}
              alt="Podgląd pliku"
              className="max-h-[80px] w-full max-w-[200px] rounded object-contain"
            />
          ) : (
            <FileText className="size-8 text-neutral-400" />
          )}

          <span className="w-full min-w-0 flex-1 truncate text-center text-sm text-neutral-700">
            {fileName ?? file?.name}
          </span>
        </>
      ) : isLoading ? (
        <>
          <Loader className="mb-3 size-8 animate-spin text-neutral-400" />
          <p className="text-sm font-medium text-neutral-700">{loadingText}</p>
        </>
      ) : (
        <>
          <Upload className="mb-3 size-8 text-neutral-400" />
          <p className="text-sm font-medium text-neutral-700">
            Przeciągnij plik lub kliknij, aby wybrać
          </p>
          {hint && <p className="mt-1 text-xs text-neutral-500">{hint}</p>}
        </>
      )}
    </div>
  );
};

PrimitiveFileInput.displayName = 'PrimitiveFileInput';

export default PrimitiveFileInput;
